import { useEffect, useState, useCallback } from "react";
import { Link } from "react-router-dom";
import { Search, Lock, ArrowRight, Sparkles } from "lucide-react";
import api from "../lib/api";
import { ui, SECTION_TAG_LABELS } from "../lib/ui";
import ReportCard from "../components/ReportCard";
import TrendingTerms from "../components/TrendingTerms";
import { useAuth } from "../context/AuthContext";

const SECTION_OPTIONS = [
  { value: "", label: "All sections" },
  ...Object.entries(SECTION_TAG_LABELS).map(([value, label]) => ({ value, label })),
];

export default function Archive() {
  const { user } = useAuth();
  const [items, setItems] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [gated, setGated] = useState(false);
  const [gateDays, setGateDays] = useState(30);
  const [q, setQ] = useState("");
  const [section, setSection] = useState("");
  const [city, setCity] = useState("");
  const [dateFrom, setDateFrom] = useState("");
  const [dateTo, setDateTo] = useState("");
  const [topicNumber, setTopicNumber] = useState("");
  const [stats, setStats] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/reports", {
        params: { q, section, city, date_from: dateFrom, date_to: dateTo, topic_number: topicNumber || 0 },
      });
      setItems(data.items);
      setTotal(data.total);
      setGated(data.gated);
      setGateDays(data.gate_days || 30);
    } finally {
      setLoading(false);
    }
  }, [q, section, city, dateFrom, dateTo, topicNumber]);

  useEffect(() => {
    api.get("/reports/stats").then((r) => setStats(r.data)).catch(() => {});
  }, []);

  useEffect(() => {
    const t = setTimeout(load, 300);
    return () => clearTimeout(t);
  }, [load]);

  return (
    <div className="max-w-6xl mx-auto px-4 sm:px-8 py-12">
      <h1 className="font-serif text-4xl sm:text-5xl font-bold text-[#8B1A1A] leading-tight">The archive</h1>
      <p className="mt-3 text-[#5A4441] max-w-2xl">Browse real, moderated exam reports from TEF Canada candidates.</p>

      {/* summary strip */}
      {stats && (
        <div className="mt-6 flex flex-wrap gap-3">
          <span className={ui.pill}>{stats.total} total reports</span>
          <span className={ui.pill}>{stats.this_month} this month</span>
          <span className={ui.pill}>Avg {stats.avg_matched != null ? stats.avg_matched : "0"} predictions matched</span>
        </div>
      )}

      {/* section quick links */}
      <div className="mt-6 flex flex-wrap gap-2" data-testid="archive-section-shortcuts">
        {Object.entries(SECTION_TAG_LABELS).map(([code, label]) => (
          <Link key={code} to={`/section/${code}`} className={ui.btnGhost + " text-sm !px-3 !py-1.5 border border-[#E8DCC8]"}>
            {label} <ArrowRight size={13} />
          </Link>
        ))}
      </div>

      {/* filters */}
      <div className="mt-6">
        <TrendingTerms section="" title="Trending across all reports" />
      </div>
      <div className={ui.card + " p-5 mt-6 grid gap-4 sm:grid-cols-2 lg:grid-cols-4"}>
        <div className="sm:col-span-2 lg:col-span-1 relative min-w-0">
          <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-[#8B1A1A]" />
          <input
            value={q}
            onChange={(e) => setQ(e.target.value)}
            placeholder="Search topics & notes"
            className={ui.input + " !pl-9"}
            data-testid="archive-search-input"
          />
        </div>
        <select value={section} onChange={(e) => setSection(e.target.value)} className={ui.input + " min-w-0"} data-testid="archive-section-filter">
          {SECTION_OPTIONS.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
        </select>
        <input value={city} onChange={(e) => setCity(e.target.value)} placeholder="City" className={ui.input + " min-w-0"} data-testid="archive-city-filter" />
        <input value={topicNumber} onChange={(e) => setTopicNumber(e.target.value.replace(/\D/g, ""))} placeholder="Topic #" className={ui.input + " min-w-0"} data-testid="archive-topic-filter" />
        <div className="flex gap-2 min-w-0 sm:col-span-2 lg:col-span-1">
          <input type="date" value={dateFrom} onChange={(e) => setDateFrom(e.target.value)} className={ui.input + " min-w-0"} data-testid="archive-date-from" title="From exam date" />
          <input type="date" value={dateTo} onChange={(e) => setDateTo(e.target.value)} className={ui.input + " min-w-0"} data-testid="archive-date-to" title="To exam date" />
        </div>
      </div>

      {gated && (
        <div className={ui.card + " p-6 mt-6 bg-[#F3E5D8] border-[#8B1A1A]/20"} data-testid="archive-gate-banner">
          <div className="flex items-start gap-3">
            <Lock size={20} className="text-[#8B1A1A] mt-0.5 shrink-0" />
            <div className="flex-1">
              <h3 className="font-serif text-lg font-semibold text-[#8B1A1A]">Members see the full archive</h3>
              <p className="text-sm text-[#671813] mt-1 max-w-2xl leading-relaxed">
                You're currently viewing reports from the last {gateDays} days. Unlock the complete history — every
                past exam report across all sections — with an Élite TEF Prep membership.
              </p>
              <div className="mt-4 flex flex-col sm:flex-row gap-3">
                <Link to="/get-access" className={ui.btnPrimary} data-testid="archive-gate-unlock-btn">
                  <Sparkles size={16} /> Unlock full archive
                </Link>
                {!user && (
                  <Link to="/login" className={ui.btnSecondary} data-testid="archive-gate-login-btn">
                    I'm already a member
                  </Link>
                )}
              </div>
            </div>
          </div>
        </div>
      )}

      {/* results */}
      <div className="mt-8">
        {loading ? (
          <p className="text-[#5A4441]">Loading…</p>
        ) : items.length === 0 ? (
          <div className={ui.card + " p-10 text-center text-[#5A4441]"}>No reports match your filters yet.</div>
        ) : (
          <>
            <p className={ui.caption + " mb-4"}>{total} report{total !== 1 ? "s" : ""}</p>
            <div className="grid gap-6 md:grid-cols-2" data-testid="archive-report-grid">
              {items.map((r) => <ReportCard key={r.id} report={r} />)}
            </div>
          </>
        )}
      </div>
    </div>
  );
}
